import { showBusyToast, hideToast } from '../actions/toast';

/**
 * How long an operation may run before it earns a busy toast.
 *
 * Below this, a click still feels acknowledged by its own result, and showing a toast would mean
 * flashing one on every fast save. Above it, an unacknowledged click starts to read as broken —
 * which is the whole problem this exists to fix.
 */
export const BUSY_TOAST_DELAY_MS = 300;

/**
 * Run an async operation, showing a persistent "in progress" toast (text + inline spinner) if — and
 * only if — it is still running after a short grace period.
 *
 * Usage, from inside a thunk:
 *
 *     return dispatch(
 *       runWithBusyToast('Toast.Duplicating', () => projects.cloneProject(…).then(…))
 *     );
 *
 * The busy toast is cleared on BOTH the success and the failure path. Clearing is tagged with the
 * generation of the busy toast itself, so an operation that ends by showing its own completion
 * message (`showToast('Toast.SketchSaved')`) does not then have that message immediately wiped —
 * the reducer drops the stale tagged hide. See reducers/toast.js.
 *
 * @param {string} busyText i18n key (or literal) for the in-progress message
 * @param {() => Promise<any>} run starts the operation and returns its promise
 * @param {number} [delayMs] grace period before the toast appears
 * @returns {(dispatch, getState) => Promise<any>} thunk resolving/rejecting as `run` does
 */
export const runWithBusyToast = (
  busyText,
  run,
  delayMs = BUSY_TOAST_DELAY_MS
) => (dispatch, getState) => {
  // A fast operation never shows a toast, and then there is nothing to clear.
  let shown = false;
  let busyGeneration;
  const timer = setTimeout(() => {
    dispatch(showBusyToast(busyText));
    shown = true;
    // Optional chaining for a store without a toast slice (partial mock state in tests): the hide
    // is then untagged, which always clears — the right fallback.
    busyGeneration = getState().toast?.generation;
  }, delayMs);

  const finish = () => {
    clearTimeout(timer);
    if (shown) dispatch(hideToast(busyGeneration));
  };

  let promise;
  try {
    promise = Promise.resolve(run());
  } catch (error) {
    // A synchronous throw out of `run` must not strand the pending toast.
    finish();
    return Promise.reject(error);
  }

  return promise.then(
    (value) => {
      finish();
      return value;
    },
    (error) => {
      finish();
      throw error;
    }
  );
};

export default runWithBusyToast;
